import Box from '@mui/material/Box';
import { useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormTextField } from '@/components/Form/FormTextField/FormTextField';
import { getFieldEntries } from '@/core/dcr/fields/registry';
import type { CommonFieldDef } from '@/core/dcr/fields/types';

import { useDcrFormContext } from '../../_context/DcrFormContext';
import { useFieldLabel } from '../../_hooks/useFieldLabel';
import { hasValue } from '../../_utils/hasValue';

import { emailListInputStyles as sx } from './EmailListInput.styles';
import { TextInput } from './TextInput';

type Props = {
	name: string;
	def: CommonFieldDef;
};

const useGroupedEntries = (name: string) => {
	const { entityType, isAvailable } = useDcrFormContext();

	return useMemo(
		() =>
			getFieldEntries(entityType).filter(
				({ key, def }) => def.groupWith === name && isAvailable(key, def)
			),
		[entityType, isAvailable, name]
	);
};

const EmailList = ({
	keys,
	defs
}: {
	keys: string[];
	defs: CommonFieldDef[];
}) => {
	const { t } = useTranslation();
	const fieldLabel = useFieldLabel();
	const { control, getValues, setValue, trigger } = useFormContext();

	const [visibleCount, setVisibleCount] = useState(() => {
		const lastFilled = keys.reduce(
			(last, key, index) => (hasValue(getValues(key)) ? index : last),
			-1
		);
		return Math.max(lastFilled + 1, 1);
	});
	const [resetToken, setResetToken] = useState(0);

	const revalidate = () => trigger(keys);

	const handleAdd = () =>
		setVisibleCount(count => Math.min(count + 1, keys.length));

	const handleRemove = (index: number) => {
		setResetToken(token => token + 1);

		for (let i = index; i < visibleCount - 1; i++) {
			setValue(keys[i], getValues(keys[i + 1]) ?? '', { shouldDirty: true });
		}
		setValue(keys[visibleCount - 1], '', { shouldDirty: true });
		setVisibleCount(count => Math.max(count - 1, 1));
		revalidate();
	};

	return (
		<Box sx={sx.group}>
			{keys.slice(0, visibleCount).map((key, index) => (
				<Controller
					key={`${key}-${visibleCount}-${resetToken}`}
					name={key}
					control={control}
					render={({ field, fieldState }) => (
						<FormTextField
							id={key}
							name={field.name}
							label={index === 0 ? t('Email', { count: visibleCount }) : ''}
							placeholder={fieldLabel(defs[index])}
							indicateError={!!fieldState.error}
							errorMessage={fieldState.error?.message}
							defaultValue={(field.value as string | undefined) ?? ''}
							onChange={value => {
								field.onChange(value);
								revalidate();
							}}
							isDirty={hasValue(field.value)}
							canBeRemoved={visibleCount > 1 || hasValue(field.value)}
							onRemove={() => handleRemove(index)}
							canAddNewItem={
								index === visibleCount - 1 && visibleCount < keys.length
							}
							onAddNewItemClick={handleAdd}
						/>
					)}
				/>
			))}
		</Box>
	);
};

export const EmailListInput = ({ name, def }: Props) => {
	const grouped = useGroupedEntries(name);

	if (grouped.length === 0) return <TextInput name={name} def={def} />;

	return (
		<EmailList
			keys={[name, ...grouped.map(entry => entry.key)]}
			defs={[def, ...grouped.map(entry => entry.def)]}
		/>
	);
};
